// assets/marquee.js
// Web Component: <custom-marquee> ... </custom-marquee>
// Expects markup:
// <custom-marquee id="ewc-marquee-{{ section.id }}" class="ewc-marquee" style="--ewc-duration: 24s;">
//   <div class="ewc-marquee__mask">
//     <div class="ewc-marquee__track">
//       <ul class="ewc-marquee__list" data-original="true">...</ul>
//     </div>
//   </div>
// </custom-marquee>

class CustomMarquee extends HTMLElement {
  constructor() {
    super();
    this._onResize = this._onResize.bind(this);
    this._build = this._build.bind(this);
    this._raf = null;
  }

  connectedCallback() {
    // cache nodes
    this.mask = this.querySelector('.ewc-marquee__mask');
    this.track = this.querySelector('.ewc-marquee__track');
    this.originalList = this.querySelector('.ewc-marquee__list[data-original="true"]');

    if (!this.mask || !this.track || !this.originalList) return;

    this._build();
    window.addEventListener('resize', this._onResize);

    document.addEventListener('shopify:section:load', (e) => {
      if (e && e.detail && this.id && e.detail.sectionId === this.id.replace('ewc-marquee-', '')) {
        this._build();
      }
    });
    document.addEventListener('shopify:section:reorder', this._build);
    document.addEventListener('shopify:block:select', this._build);
    document.addEventListener('shopify:block:deselect', this._build);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
  }

  _onResize() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(this._build);
  }

  _build() {
    // Remove any previously cloned lists
    this.track.querySelectorAll('.ewc-marquee__list:not([data-original="true"])').forEach((n) => n.remove());

    // Force layout after cleanup
    const maskWidth = this.mask.clientWidth;
    const setWidth = this.originalList.scrollWidth;

    // Set loop distance to exactly one set width
    this.style.setProperty('--ewc-loop-distance', setWidth + 'px');

    // Clone enough times so that while we translate one full set,
    // the remaining content still fully covers the viewport.
    // Ensure total width >= maskWidth + setWidth, add one extra as buffer.
    let total = setWidth;
    let clones = 0;
    while (total < maskWidth + setWidth) {
      const clone = this.originalList.cloneNode(true);
      clone.removeAttribute('data-original');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', '-1');
        link.setAttribute('aria-hidden', 'true');
      });
      this.track.appendChild(clone);
      total += setWidth;
      clones++;
      if (clones > 12) break;
    }

    // Restart animation cleanly
    const prev = this.track.style.animation;
    this.track.style.animation = 'none';
    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    this.track.offsetWidth;
    this.track.style.animation = prev || '';
  }
}

// Define once
if (!customElements.get('custom-marquee')) {
  customElements.define('custom-marquee', CustomMarquee);
}